// ---------------------------------------------------------------------------
// TanStack Query settings.
//
// The defaults are tuned for a shop till rather than a public website:
//   * do not refetch every time the window regains focus - a cashier switching
//     between windows should not trigger a burst of requests,
//   * never retry a permission or validation failure, because retrying a 403
//     produces exactly the same 403,
//   * do retry a genuine network blip, since the shop's connection is not
//     always perfect.
// ---------------------------------------------------------------------------

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './apiClient';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // ALWAYS TRY. TanStack's default `networkMode: 'online'` pauses every
      // query and mutation while `navigator.onLine` is false - which on the
      // shop PC is Windows saying the internet is gone, while the till's own
      // server answers on 127.0.0.1 and the printers sit on the LAN. Whether
      // the server is reachable is decided by asking it (the health probe in
      // syncEngine), never by the operating system's opinion of the internet.
      networkMode: 'always',
      // Data is considered fresh for a minute. Product lists and exchange
      // rates do not change second by second.
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // Retrying these can never succeed.
          if (!error.isRetryable) return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    },
    mutations: {
      // See above: a sale, a payment or a print job must reach the server
      // whenever the server can be reached, not whenever Windows thinks the
      // internet is up.
      networkMode: 'always',
      // A write is never retried automatically. Sending a sale twice because
      // the first response was slow would be far worse than showing an error.
      retry: false,
    },
  },
});

/// Query keys in one place, so a cache entry is never invalidated by a typo.
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
    pinUsers: ['auth', 'pin-users'] as const,
  },
  catalog: {
    categories: ['categories'] as const,
    products: (filters?: Record<string, unknown>) => ['products', filters ?? {}] as const,
    product: (id: string) => ['product', id] as const,
    priceHistory: (id: string) => ['product', id, 'price-history'] as const,
  },
  system: {
    exchangeRate: ['exchange-rate', 'current'] as const,
    publicSettings: ['settings', 'public'] as const,
    currentShift: ['shifts', 'current'] as const,
    dashboard: ['dashboard', 'summary'] as const,
    employees: ['employees'] as const,
    permissions: ['permissions'] as const,
    audit: (filters?: Record<string, unknown>) => ['audit', filters ?? {}] as const,
  },
};
